import React, { useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCsv } from '../../utils/exportCsv';
import type { BackOfficeKudosResponse, BackOfficeKudosSummary, KudosRecord } from '@crewcast/shared';
import LoadMoreButton from '../../components/LoadMoreButton';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';

const SOURCE_LABELS: Record<KudosRecord['source'], string> = {
  'game-plan': 'Game Plan',
  'employee-profile': 'Employee profile',
  'employees-page': 'Employees page',
  unknown: '—',
};

/** YYYY-MM-DD, UTC, matching how the API reads dateFrom/dateTo. */
function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthStartYmd(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

type KudosRow = BackOfficeKudosResponse['kudos'][number];

/**
 * Kudos audit trail: every kudos sent, who sent it to whom, the message and
 * any bonus points. Read-only, newest first, 50 at a time.
 */
export default function BackOfficeKudosPage() {
  const { token } = useAuth();
  const [companies, setCompanies] = useState<{ companyId: string; displayName: string }[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [dateFrom, setDateFrom] = useState(monthStartYmd());
  const [dateTo, setDateTo] = useState(todayYmd());
  // Filters take effect on Apply
  const [applied, setApplied] = useState({ companyId: '', dateFrom: monthStartYmd(), dateTo: todayYmd() });
  const [summary, setSummary] = useState<BackOfficeKudosSummary | null>(null);

  const params = useMemo(() => {
    const p: Record<string, string> = { dateFrom: applied.dateFrom, dateTo: applied.dateTo };
    if (applied.companyId) p.companyId = applied.companyId;
    return p;
  }, [applied]);
  const { items: kudos, loading, loadingMore, hasMore, error, loadMore, refresh } = usePaginatedFetch<KudosRow>({
    url: `${authConfig.apiUrl}/api/backoffice/kudos`,
    token,
    dataKey: 'kudos',
    limit: 50,
    params,
  });

  // Load company list once for the filter dropdown
  useEffect(() => {
    async function loadCompanies() {
      try {
        if (!token) return;
        const res = await fetch(`${authConfig.apiUrl}/api/backoffice/companies?pageSize=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok) setCompanies(body.companies || []);
      } catch {
        // Non-fatal
      }
    }
    loadCompanies();
  }, []); // eslint-disable-line

  // Totals need every row in the range, so they're only fetched for one
  // account (one cheap query); across all accounts they'd mean a full read.
  useEffect(() => {
    setSummary(null);
    if (!applied.companyId || !token) return;
    const qs = new URLSearchParams({ ...params, summary: '1' });
    fetch(`${authConfig.apiUrl}/api/backoffice/kudos?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => body && setSummary(body))
      .catch(() => {});
  }, [params]); // eslint-disable-line

  function handleApply(e: React.FormEvent) {
    e.preventDefault();
    setApplied({ companyId, dateFrom, dateTo });
  }

  function handleExport() {
    exportToCsv(
      `crewcast-kudos-${applied.dateFrom}-${applied.dateTo}${applied.companyId ? `-${applied.companyId}` : ''}`,
      ['sentAt', 'companyName', 'fromName', 'fromUserId', 'toName', 'toUserId', 'points', 'message', 'source', 'gamePlanItemDone', 'kudosId'],
      kudos as unknown as Record<string, unknown>[]
    );
  }

  return (
    <>
      <div className="bo-page-header">
        <h1 className="bo-page-title">Kudos</h1>
        <p className="bo-page-subtitle">Every kudos sent by admins, with any bonus points (audit trail)</p>
      </div>

      {applied.companyId && (
        <div className="bo-summary-row">
          <div className="bo-summary-item">
            <div className="bo-summary-item-label">Kudos sent</div>
            <div className="bo-summary-item-value">{summary ? summary.totalKudos : '—'}</div>
          </div>
          <div className="bo-summary-item">
            <div className="bo-summary-item-label">Bonus points granted</div>
            <div className="bo-summary-item-value">{summary ? summary.totalPoints.toLocaleString() : '—'}</div>
          </div>
        </div>
      )}

      <form className="bo-filters" onSubmit={handleApply}>
        <div className="bo-filter-group">
          <label className="bo-filter-label">From</label>
          <input className="bo-filter-input" type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">To</label>
          <input className="bo-filter-input" type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Account</label>
          <select className="bo-filter-select crewcast-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">All accounts</option>
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>{c.displayName}</option>
            ))}
          </select>
        </div>
        <div className="bo-filter-actions">
          <button type="submit" className="bo-btn bo-btn-primary" disabled={loading}>
            Apply
          </button>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={refresh} disabled={loading}>
            <RefreshCw size={14} />
          </button>
          <button
            type="button"
            className="bo-btn bo-btn-secondary"
            onClick={handleExport}
            disabled={loading || kudos.length === 0}
            title="Exports the rows loaded so far"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </form>

      <div className="bo-table-card">
        <div className="bo-table-header">
          <span className="bo-table-title">
            Kudos history
            <span className="bo-table-count">({kudos.length}{hasMore ? '+' : ''})</span>
          </span>
        </div>

        {loading ? (
          <div className="bo-loading">Loading...</div>
        ) : error ? (
          <div className="bo-error">{error}</div>
        ) : kudos.length === 0 ? (
          <div className="bo-empty">No kudos in this date range.</div>
        ) : (
          <div className="bo-table-wrapper">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>Sent</th>
                  <th>Account</th>
                  <th>From</th>
                  <th>To</th>
                  <th className="num">Points</th>
                  <th>Message</th>
                  <th>Sent from</th>
                </tr>
              </thead>
              <tbody>
                {kudos.map((k) => (
                  <tr key={k.kudosId}>
                    <td>{new Date(k.sentAt).toLocaleString()}</td>
                    <td>{k.companyName}</td>
                    <td>{k.fromName}</td>
                    <td>{k.toName}</td>
                    <td className="num">{k.points > 0 ? <strong>+{k.points.toLocaleString()}</strong> : '—'}</td>
                    <td className="bo-kudos-message">{k.message}</td>
                    <td>
                      {SOURCE_LABELS[k.source] ?? '—'}
                      {k.gamePlanItemDone && <span className="bo-badge bo-badge-green" style={{ marginLeft: 6 }}>Top contributor</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />
    </>
  );
}
